// When the acts findings name happened: the time of each finding's cited
// messages, which is when the suspicious thing was said or done (a finding's
// own time is when review finished, which says nothing about the act). Read
// in batches as findings come into view, kept for the session, and asked
// again only where a read was refused.
import { useEffect, useRef, useState } from "react";
import { MESSAGE_TIME_BATCH, fetchMessageTimes } from "./apiClient.js";
import { parseServerTimestamp } from "./traceUtils.js";

const lower = (v) => String(v ?? "").trim().toLowerCase();
// How long before its findings were reviewed a session's messages are looked
// for: messages are windowed by when their session started, and a narrow
// window keeps each read cheap. A session older than this reads no time.
const SESSION_LOOKBACK_MS = 7 * 86_400_000;
// What the reads found, kept for the browser session so a console opened again
// does not read again: each message's time, and every message asked (found or
// not). A refused read's messages are asked again.
const TIMES = new Map();
const ASKED = new Set();
const FLUSH_MS = 150;
export function _resetEventTimesForTests() { TIMES.clear(); ASKED.clear(); }

/**
 * Message times for `findings` (each { session, completedAt, messages:
 * [message id] }), as Map(message id -> ms), filled as the reads land: one
 * read per session (the gateway takes messages only by an exact session),
 * queued with the other trace reads, over the week before its findings were
 * reviewed. `window` is the read window ({ fromMs, toMs }) the findings came
 * from, for findings with no review time.
 */
export function useEventTimes(findings, window) {
  const [times, setTimes] = useState(() => new Map(TIMES));
  const alive = useRef(true);
  const abort = useRef(null);
  const flush = useRef(null);
  const pending = useRef(new Set());
  // Set again on each mount: development runs effects twice, cleanup between.
  useEffect(() => {
    alive.current = true;
    abort.current = new AbortController();
    return () => {
      alive.current = false;
      abort.current.abort();
      for (const m of pending.current) ASKED.delete(m);
      pending.current.clear();
      clearTimeout(flush.current);
      flush.current = null;
    };
  }, []);
  const publish = () => {
    if (!alive.current || flush.current) return;
    flush.current = setTimeout(() => {
      flush.current = null;
      if (alive.current) setTimes(new Map(TIMES));
    }, FLUSH_MS);
  };
  const want = findings.filter((f) => (f.messages || []).some((m) => m && !ASKED.has(lower(m))));
  const key = want.map((f) => f.id).join(",");
  useEffect(() => {
    if (!want.length || !Number.isFinite(window?.fromMs) || !Number.isFinite(window?.toMs)) return;
    const bySession = new Map();
    for (const f of want) {
      const ids = (f.messages || []).map(lower).filter((m) => m && !ASKED.has(m));
      if (!ids.length || !f.session) continue;
      for (const m of ids) ASKED.add(m);
      const session = lower(f.session);
      const reviewed = parseServerTimestamp(f.completedAt);
      const one = bySession.get(session) || { ids: [], fromMs: Infinity, toMs: -Infinity };
      one.ids.push(...ids);
      one.fromMs = Math.min(one.fromMs, Number.isFinite(reviewed) ? reviewed - SESSION_LOOKBACK_MS : window.fromMs);
      one.toMs = Math.max(one.toMs, Number.isFinite(reviewed) ? reviewed : window.toMs);
      bySession.set(session, one);
    }
    for (const [session, { ids: all, fromMs, toMs }] of bySession) {
      for (let i = 0; i < all.length; i += MESSAGE_TIME_BATCH) {
        const ids = all.slice(i, i + MESSAGE_TIME_BATCH);
        for (const m of ids) pending.current.add(m);
        const signal = abort.current?.signal;
        fetchMessageTimes({ session, messageIds: ids, fromMs, toMs, signal }).then((got) => {
          for (const m of ids) pending.current.delete(m);
          for (const [id, at] of got) TIMES.set(id, at);
          if (got.size) publish();
        }, () => {
          if (signal?.aborted) return;
          for (const m of ids) { pending.current.delete(m); ASKED.delete(m); }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, window?.fromMs, window?.toMs]);
  return times;
}
